/**
 * Gateway Power co-branding for every rendered or exported report.
 *
 * Gateway Power LLC is the legal operator of Datacrate (gatewaypower.io). The
 * owner asked (2026-09-30) for ALL reports to carry Gateway Power branding
 * "as well" and "subtle": Datacrate stays the report identity; Gateway Power
 * appears as a small platform credit — the G mark plus one muted line — on the
 * cover / title page, never competing with the
 * Datacrate identity, the report title or the content.
 *
 * ONE wording for every format (viewer, PDF, DOCX, PPTX, economic brief HTML +
 * DOCX) so the surfaces cannot drift: {@link REPORT_PLATFORM_CREDIT_LINE}.
 *
 * Artwork is the vendored 2026-09-29 brand export
 * (src/components/marketing/brand/gateway-power/, README.txt) — reused, never
 * recoloured or redrawn:
 *   - the SVG marks below are the exact file text of gateway-power-logo-light.svg
 *     (white / light pages) and gateway-power-logo.svg (dark covers);
 *   - raster formats use ./gatewayPowerMarkPng.ts, a 128 px downscale of the
 *     brand PNG export.
 * gatewayPowerReportBrand.test.ts pins both to the vendored files.
 *
 * Pure module (no React, no DOM, no Node built-ins) so the browser viewer, the
 * browser exporters and the server artifact projector all read the same bytes.
 */
import { GATEWAY_POWER_MARK_LIGHT_PNG_BASE64 } from './gatewayPowerMarkPng';

/** The operating company, as it names itself on its own site. */
export const GATEWAY_POWER_NAME = 'Gateway Power';

/**
 * The one credit line every report surface prints next to the small G mark.
 * (No report surface prints a legal entity or copyright line; the operator's
 * legal name, Gateway Power LLC, lives with the marketing site's
 * LEGAL_ENTITY in src/components/marketing/legalEntity.tsx.)
 */
export const REPORT_PLATFORM_CREDIT_LINE = `Datacrate · A ${GATEWAY_POWER_NAME} platform`;

/**
 * Which artwork a surface needs: the light-background artwork (brand navy +
 * teal-600) on white / light pages, the dark artwork (white + brand teal) on
 * dark covers and the dark reader. Named by the SURFACE, like
 * CoverCustomerLogo's tone, so a caller never has to remember that the
 * "-light" file is the one for light backgrounds.
 */
export type GatewayPowerMarkTone = 'on-light' | 'on-dark';

/** Exact text of brand/gateway-power/gateway-power-logo-light.svg. */
export const GATEWAY_POWER_MARK_SVG_ON_LIGHT = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="1024" height="1024" viewBox="8 12 72 72" role="img" aria-label="Gateway Power logo mark (light background)">
<title>Gateway Power logo mark (light background)</title>
<defs>
  <clipPath id="gateway-mark-white"><polygon points="0,0 89,0 0,89"/></clipPath>
  <clipPath id="gateway-mark-teal"><polygon points="0,95 95,0 120,0 120,120 0,120"/></clipPath>
</defs>
<path d="M8 12H80V26H22V70H66V55H44V51L54 41H80V84H8Z" fill="#0B1118" clip-path="url(#gateway-mark-white)"/>
<path d="M8 12H80V26H22V70H66V55H44V51L54 41H80V84H8Z" fill="#0D9488" clip-path="url(#gateway-mark-teal)"/>
</svg>
`;

/** Exact text of brand/gateway-power/gateway-power-logo.svg. */
export const GATEWAY_POWER_MARK_SVG_ON_DARK = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="1024" height="1024" viewBox="8 12 72 72" role="img" aria-label="Gateway Power logo mark">
<title>Gateway Power logo mark</title>
<defs>
  <clipPath id="gateway-mark-white"><polygon points="0,0 89,0 0,89"/></clipPath>
  <clipPath id="gateway-mark-teal"><polygon points="0,95 95,0 120,0 120,120 0,120"/></clipPath>
</defs>
<path d="M8 12H80V26H22V70H66V55H44V51L54 41H80V84H8Z" fill="#FFFFFF" clip-path="url(#gateway-mark-white)"/>
<path d="M8 12H80V26H22V70H66V55H44V51L54 41H80V84H8Z" fill="#14B8A6" clip-path="url(#gateway-mark-teal)"/>
</svg>
`;

const MARK_DATA_URI: Record<GatewayPowerMarkTone, string> = {
  'on-light': `data:image/svg+xml;charset=utf-8,${encodeURIComponent(GATEWAY_POWER_MARK_SVG_ON_LIGHT)}`,
  'on-dark': `data:image/svg+xml;charset=utf-8,${encodeURIComponent(GATEWAY_POWER_MARK_SVG_ON_DARK)}`,
};

/**
 * The vendored mark as an <img> source. An image (not inline SVG) keeps the
 * artwork byte-identical to the brand file and keeps its fixed clip-path ids
 * out of the host document, where two inline copies would collide.
 */
export function gatewayPowerMarkDataUri(tone: GatewayPowerMarkTone): string {
  return MARK_DATA_URI[tone];
}

/** Light-background mark as PNG bytes, for DOCX ImageRun. */
export function gatewayPowerMarkLightPngBytes(): Uint8Array {
  if (typeof Buffer !== 'undefined') {
    return Uint8Array.from(Buffer.from(GATEWAY_POWER_MARK_LIGHT_PNG_BASE64, 'base64'));
  }
  const binary = atob(GATEWAY_POWER_MARK_LIGHT_PNG_BASE64);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

/** Light-background mark in pptxgenjs' data form (no leading "data:"). */
export function gatewayPowerMarkLightPptxData(): string {
  return `image/png;base64,${GATEWAY_POWER_MARK_LIGHT_PNG_BASE64}`;
}
